import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  AlertCircle,
  GraduationCap,
  Laptop
} from "lucide-react";
import { 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Snackbar,
  Tooltip
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PaymentDetailsModal from "./PaymentDetailsModal";

const AdvanceBook = ({ onClose, propInstituteBookings = null, subTabs, forcedEventType }) => {
  const [instituteBookings, setInstituteBookings] = useState(propInstituteBookings || null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentBooking, setSelectedPaymentBooking] = useState(null);
  const [domainsList, setDomainsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calendarAvailability, setCalendarAvailability] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const getGlobalActiveBooking = () => {
    if (!instituteBookings) return null;

    const ab = instituteBookings.advance_booking || {};
    for (const key of Object.keys(ab)) {
      if (key === "canceled" || key === "cancelled") continue;
      if (Array.isArray(ab[key]) && ab[key].length > 0) {
        return { source: "booking", status: key, ...ab[key][0] };
      }
    }

    const interest = instituteBookings.interest || {};
    for (const key of Object.keys(interest)) {
      if (key === "cancelled" || key === "canceled") continue;
      if (Array.isArray(interest[key]) && interest[key].length > 0) {
        return { source: "interest", status: key, ...interest[key][0] };
      }
    }

    return null;
  };

  const activeInterest = getGlobalActiveBooking();

  const formatDateString = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusInfo = (statusVal) => {
    const statusMap = {
      0: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      "0": { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      pending: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      1: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      "1": { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      approved: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      accepted: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      3: { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      "3": { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      booked: { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      4: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
      "4": { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
      paid: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
    };
    return statusMap[statusVal] || { label: "Unknown", colorClass: "text-slate-600 bg-slate-50 border-slate-200" };
  };

  // Custom toast notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Form State
  const [formData, setFormData] = useState({
    trainerId: "0",
    state: "",
    institute: "0",
    eventType: forcedEventType || "",
    domain: "",
  });

  useEffect(() => {
    if (forcedEventType) {
      setFormData(prev => ({ ...prev, eventType: forcedEventType, domain: "" }));
      setSelectedDates([]);
    }
  }, [forcedEventType]);

  // Selected dates for booking
  const [selectedDates, setSelectedDates] = useState([]); // [{ date: 'YYYY-MM-DD', availability_id: number }]
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);



  // Month navigation (shows 3 months at once)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthOffset, setMonthOffset] = useState(0);
  const maxOffset = 4;

  const api = axios.create({
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  useEffect(() => {
    // Immediately set propInstituteBookings if it exists so we don't wait for domain API
    if (propInstituteBookings) {
      setInstituteBookings(propInstituteBookings);
    }
    
    const fetchDomainsAndBookings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("accessToken");
        // Fetch domains based on eventType only if opening the booking modal (onClose exists)
        if (!!onClose) {
          const domainsResponse = await api.get(`/event/domainLists/${formData.eventType}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          setDomainsList(domainsResponse.data.domains || []);
        }

        if (!propInstituteBookings && !onClose) {
          try {
            const bookingsResponse = await api.get(`/event/get-interests/${formData.eventType}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
            const bookingData = bookingsResponse.data;
            
            if (Array.isArray(bookingData) && bookingData.length > 0) {
              const interestGroup = {};
              const statusMap = {
                0: "pending",
                1: "approved",
                2: "cancelled",
                3: "booked",
                4: "paid",
              };
              bookingData.forEach(item => {
                const statusKey = statusMap[item.status] || "pending";
                if (!interestGroup[statusKey]) interestGroup[statusKey] = [];
                interestGroup[statusKey].push(item);
              });
              
              setInstituteBookings({
                interest: interestGroup
              });
            } else if (bookingData && typeof bookingData === 'object' && !Array.isArray(bookingData)) {
              const statusMap = {
                0: "pending",
                1: "approved",
                2: "cancelled",
                3: "booked",
                4: "paid",
              };
              const statusKey = statusMap[bookingData.status] || "pending";
              setInstituteBookings({
                interest: {
                  [statusKey]: [bookingData]
                }
              });
            } else {
              setInstituteBookings(null);
            }
          } catch (e) {
            console.error("Failed to fetch active bookings:", e);
          }
        }
      } catch (error) {
        console.error("Failed to fetch domains or active bookings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDomainsAndBookings();
  }, [propInstituteBookings, formData.eventType]);

  // getGlobalActiveBooking removed, replaced by state

  // selectedDomain effect removed

  useEffect(() => {
    setSelectedDates([]);
    setMonthOffset(0);
  }, [formData.domain]);

  const fetchCalendarAvailability = async () => {
    if (!formData.domain) {
      setCalendarAvailability(null);
      return;
    }
    try {
      setLoadingCalendar(true);
      const token = localStorage.getItem("accessToken");
      const response = await api.post(
        "/event/institute/calendar-availability",
        {
          domain_id: parseInt(formData.domain),
          event_type: formData.eventType,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCalendarAvailability(response.data || null);
    } catch (error) {
      console.error("Failed to fetch calendar availability:", error);
      setCalendarAvailability(null);
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchCalendarAvailability();
  }, [formData.domain, formData.eventType]);



  // Format date key as 'YYYY-MM-DD'
  const formatDateKey = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getApiStatusForDate = (date) => {
    if (!calendarAvailability || !calendarAvailability.dates) return null;
    const dateKey = formatDateKey(date);
    const found = calendarAvailability.dates.find(item => item.date === dateKey);
    if (found) {
      if (parseInt(found.available_trainers) === 0) {
        return found;
      }
      const statusVal = parseInt(found.status);
      if ([0, 1, 2, 3, 4].includes(statusVal)) {
        const itemDomainId = found.domain_id || found.domain?.domain_id;
        if (itemDomainId && itemDomainId.toString() === formData.domain.toString()) {
          return found;
        }
        return null;
      }
      if (statusVal === 5) {
        return found;
      }
    }
    return null;
  };

  const getInstituteStatusForDate = (date) => {
    if (!instituteBookings) return null;
    const dateKey = formatDateKey(date);

    const findInRange = (list) => {
      if (!list) return null;
      return list.find(item => dateKey >= item.start_date && dateKey <= item.end_date);
    };

    if (date.getDay() === 0) return null; // Sundays are always OFF

    const ab = instituteBookings.advance_booking || {};
    const interest = instituteBookings.interest || {};

    // 1. Confirmed Bookings (booked)
    if (findInRange(interest.booked)) return { type: "interest", status: "booked", label: "Booked" };

    // 2. Accepted / Approved
    if (findInRange(ab.accepted)) return { type: "advance_booking", status: "accepted", label: "Accepted" };
    if (findInRange(interest.approved)) return { type: "interest", status: "approved", label: "Approved" };

    // 3. Pending
    if (findInRange(ab.pending)) return { type: "advance_booking", status: "pending", label: "Pending" };
    if (findInRange(interest.pending)) return { type: "interest", status: "pending", label: "Pending Interest" };

    // 4. Rejected
    if (findInRange(ab.rejected)) return { type: "advance_booking", status: "rejected", label: "Rejected" };

    // 5. Cancelled / Canceled
    if (findInRange(ab.canceled)) return { type: "advance_booking", status: "canceled", label: "Canceled" };
    if (findInRange(interest.cancelled)) return { type: "interest", status: "cancelled", label: "Cancelled" };

    return null;
  };

  const getResolvedStatusForDate = (date) => {
    const instStatusObj = getInstituteStatusForDate(date);
    if (instStatusObj) {
      return instStatusObj.status;
    }
    return null;
  };

  const currentSelectedDomainObj = useMemo(() => {
    if (!formData.domain || !domainsList) return null;
    return domainsList.find(d => (d.domain_id || d.id).toString() === formData.domain.toString());
  }, [formData.domain, domainsList]);

  const interestDetails = useMemo(() => {
    if (!calendarAvailability || !calendarAvailability.dates) return [];
    
    const validDates = calendarAvailability.dates
      .filter(item => {
        const statusVal = parseInt(item.status);
        return [0, 1, 2, 3, 4].includes(statusVal);
      })
      .map(item => ({
        ...item,
        parsedDate: new Date(item.date)
      }))
      .sort((a, b) => {
        const aDomId = (a.domain_id || a.domain?.domain_id || "").toString();
        const bDomId = (b.domain_id || b.domain?.domain_id || "").toString();
        if (aDomId !== bDomId) {
          return aDomId.localeCompare(bDomId);
        }
        return a.parsedDate - b.parsedDate;
      });

    if (validDates.length === 0) return [];

    const groups = [];
    let currentGroup = [validDates[0]];

    for (let i = 1; i < validDates.length; i++) {
      const prev = validDates[i - 1];
      const curr = validDates[i];
      
      const diffTime = Math.abs(curr.parsedDate - prev.parsedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const prevDomainId = prev.domain_id || prev.domain?.domain_id;
      const currDomainId = curr.domain_id || curr.domain?.domain_id;

      const isConsecutive = 
        curr.status === prev.status && 
        prevDomainId?.toString() === currDomainId?.toString() &&
        (diffDays === 1 || (diffDays === 2 && (prev.parsedDate.getDay() === 6 || prev.parsedDate.getDay() === 0)));

      if (isConsecutive) {
        currentGroup.push(curr);
      } else {
        groups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    groups.push(currentGroup);

    const statusLabels = {
      0: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      1: { label: "Accepted", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      2: { label: "Rejected", colorClass: "text-purple-700 bg-purple-50 border-purple-200" },
      3: { label: "Booked", colorClass: "text-red-700 bg-red-50 border-red-200" },
      4: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" }
    };

    const formatDateString = (d) => {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    };

    return groups.map((group, index) => {
      const start = group[0].parsedDate;
      const end = group[group.length - 1].parsedDate;
      const dateRangeStr = start.getTime() === end.getTime() 
        ? formatDateString(start) 
        : `${formatDateString(start)} - ${formatDateString(end)}`;
      
      const statusVal = parseInt(group[0].status);
      const statusObj = statusLabels[statusVal] || { label: "Unknown", colorClass: "text-slate-600 bg-slate-50 border-slate-200" };

      const itemDomainId = group[0].domain_id || group[0].domain?.domain_id;
      const matchedDomain = domainsList.find(d => (d.domain_id || d.id).toString() === itemDomainId?.toString());
      const domainName = matchedDomain 
        ? (matchedDomain.domain_name || matchedDomain.name) 
        : (group[0].domain_name || group[0].domain?.domain_name || group[0].domain?.name || calendarAvailability.domain_name || "N/A");

      return {
        id: index,
        domainName: domainName,
        dateRange: dateRangeStr,
        statusLabel: statusObj.label,
        statusColorClass: statusObj.colorClass
      };
    });
  }, [calendarAvailability, domainsList]);

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isFutureDateLimit = (date) => {
    const today = new Date();
    const currentMonthIdx = today.getMonth();
    const maxAllowedMonthIdx = currentMonthIdx + 5; // next 6 months (current month + 5)
    const maxYear = today.getFullYear() + Math.floor(maxAllowedMonthIdx / 12);
    const maxMonth = maxAllowedMonthIdx % 12;

    const d = new Date(date);
    const limitDate = new Date(maxYear, maxMonth + 1, 0, 23, 59, 59, 999);
    return d > limitDate;
  };

  const getBlockDates = (startDate) => {
    const dates = [];
    let current = new Date(startDate);
    const count = formData.eventType === "fdp" ? 6 : 3;

    while (dates.length < count) {
      if (current.getDay() !== 0) { // Skip Sunday
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const isBlockDisabled = (startDate) => {
    const blockDates = getBlockDates(startDate);
    if (blockDates.length === 0) return true;

    for (const d of blockDates) {
      if (isPastDate(d) || isFutureDateLimit(d)) {
        return true;
      }
      if (getApiStatusForDate(d)) {
        return true;
      }
      const status = getResolvedStatusForDate(d);
      if (status && status !== "cancelled" && status !== "canceled") {
        return true;
      }
    }
    return false;
  };

  const isRestrictedWeek = (date) => {
    const today = new Date();
    // Move today to Monday of current week
    const currentMonday = new Date(today);
    const currentDay = currentMonday.getDay();
    const diffToMonday = currentMonday.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
    currentMonday.setDate(diffToMonday);
    currentMonday.setHours(0, 0, 0, 0);

    // Week after next Monday (start of valid selectable range)
    const validStartMonday = new Date(currentMonday);
    validStartMonday.setDate(currentMonday.getDate() + 14);

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < validStartMonday;
  };

  const isDateDisabled = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    if (isPastDate(d)) return true;
    if (d.getDay() === 0) return true; // Sundays disabled
    if (isFutureDateLimit(d)) return true;
    
    // 1. Disable current week and next week
    if (isRestrictedWeek(d)) return true;

    // 2. FDP: Only Monday
    if (formData.eventType === "fdp" && d.getDay() !== 1) {
      return true;
    }

    // 3. Tech Camp: Only Monday and Thursday
    if (formData.eventType === "tech_camp" && d.getDay() !== 1 && d.getDay() !== 4) {
      return true;
    }

    // Check backend calendar availability response
    if (getApiStatusForDate(d)) {
      return true;
    }

    // Disable if already booked or has active resolved status
    const status = getResolvedStatusForDate(d);
    if (status && status !== "cancelled" && status !== "canceled") {
      return true;
    }

    // Instead of disabling only if block is disabled, we'll let handleDateClick handle block errors 
    // to give better feedback, but we can return true here if we want strict UI disabling.
    // For now we just return false if it passes the date-level checks.
    return false;
  };

  // Date selection logic
  const handleDateClick = (clickedDate, isCellDisabled) => {
    const globalActive = activeInterest;
    if (globalActive) {
      let statusMsg = "";
      if (globalActive.status === "accepted" || globalActive.status === "approved" || globalActive.status === "paid") {
        statusMsg = "Accepted / Approved";
      } else if (globalActive.status === "pending" || globalActive.status === "inprogress" || globalActive.status === "payment_pending") {
        statusMsg = "Pending";
      } else if (globalActive.status === "rejected") {
        statusMsg = "Rejected";
      } else if (globalActive.status === "booked") {
        statusMsg = "Booked";
      }
      showToast(`You already have a booking / interest with status: ${statusMsg}.`, "warning");
      return;
    }

    if (!formData.trainerId || !formData.eventType) return;

    // Check if there is an active resolved status on or after today for this institute
    const instStatusObj = getInstituteStatusForDate(clickedDate);
    const isPast = isPastDate(clickedDate);

    if (instStatusObj && instStatusObj.status !== 'cancelled' && instStatusObj.status !== 'canceled' && !isPast) {
      const resolvedStatus = instStatusObj.status;
      let statusMsg = "";
      if (resolvedStatus === "accepted" || resolvedStatus === "approved" || resolvedStatus === "paid") {
        statusMsg = "Accepted / Approved";
      } else if (resolvedStatus === "pending" || resolvedStatus === "inprogress" || resolvedStatus === "payment_pending") {
        statusMsg = "Pending";
      } else if (resolvedStatus === "rejected") {
        statusMsg = "Rejected";
      } else if (resolvedStatus === "booked") {
        statusMsg = "Booked";
      }
      showToast(`You already have a booking request for this date that is ${statusMsg}.`, "warning");
      return;
    }

    if (isCellDisabled) return;

    // Unified Selection Condition
    const blockDates = getBlockDates(clickedDate);
    if (blockDates.length === 0) return;
    
    // Check if any date in block is disabled
    if (isBlockDisabled(clickedDate)) {
      showToast("Some dates in this block are unavailable.", "warning");
      return;
    }

    const blockStartKey = formatDateKey(blockDates[0]);
    const isAlreadySelected = selectedDates.some((s) => s.date === blockStartKey);

    if (isAlreadySelected) {
      setSelectedDates([]);
      return;
    }

    const selected = [];
    for (const d of blockDates) {
      const key = formatDateKey(d);
      selected.push({
        date: key,
        availability_id: null,
      });
    }

    setSelectedDates(selected);
  };

  // Check if a date is currently selected
  const isDateSelected = (date) => {
    const key = formatDateKey(date);
    return selectedDates.some((d) => d.date === key);
  };

  const handleBookSelectedClick = () => {
    if (selectedDates.length === 0) {
      showToast("Please select at least one date from the calendar.", "warning");
      return;
    }
    setShowConfirmPopup(true);
  };

  // Submit Booking
  const handleSubmitBooking = async () => {
    if (selectedDates.length === 0) {
      showToast("Please select at least one date from the calendar.", "warning");
      return;
    }
    if (!formData.eventType || !formData.domain) {
      showToast("Please fill all required fields marked with *", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        dates: selectedDates.map((d) => d.date),
        domain_id: parseInt(formData.domain),
        is_paid: 1,
      };

      const response = await api.post("/event/institute/submit-interest2", payload);
      const isSuccess = response.status === 200 && 
                        response.data?.status !== false && 
                        response.data?.status !== "error" && 
                        response.data?.success !== false;

      if (isSuccess) {
        showToast(response.data?.message || "Interest submitted successfully!", "success");
        setSelectedDates([]);
        await fetchCalendarAvailability();
        // Refresh bookings using the new get-interests API
        if (!propInstituteBookings) {
          try {
            const token = localStorage.getItem("accessToken");
            const bookingsResponse = await api.get(`/event/get-interests/${formData.eventType}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
            const bookingData = bookingsResponse.data;
            let hasActiveBooking = false;
            let activeBookingData = null;
            if (bookingData && typeof bookingData !== 'string') {
              hasActiveBooking = true;
              activeBookingData = bookingData;
            } else if (bookingData && typeof bookingData === 'string' && bookingData !== "No interests found.") {
              hasActiveBooking = true;
              activeBookingData = bookingData;
            }

            if (hasActiveBooking) {
              setInstituteBookings({
                interest: {
                  [activeBookingData.status || 0]: [activeBookingData]
                }
              });
            } else {
              setInstituteBookings(null);
            }
          } catch (e) {
            console.error("Failed to refresh bookings:", e);
          }
        }
        if (onClose) onClose(true);
      } else {
        showToast(response.data?.message || "Failed to submit booking.", "error");
      }
    } catch (error) {
      console.error("Booking error:", error);
      let errorMsg = "Failed to submit booking.";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail[0].msg;
        } else if (typeof error.response.data.detail === "string") {
          errorMsg = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        if (typeof error.response.data.message === "string") {
          errorMsg = error.response.data.message;
        }
      }
      showToast(errorMsg, "error");
    } finally {
      setSubmitting(false);
    }
  };



  // Helper: Get list of 5 months starting from the current month
  const visibleMonths = useMemo(() => {
    const list = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11
    for (let i = 0; i < 5; i++) {
      const d = new Date(currentYear, currentMonth + i, 1);
      list.push(d);
    }
    return list;
  }, []);

  // Generate 42 cells grid for calendar
  const generateDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const prevMonthDays = [];
    for (let i = 0; i < startDay; i++) {
      const d = new Date(year, month, i - startDay + 1);
      prevMonthDays.push({ date: d, day: d.getDate(), isCurrentMonth: false });
    }

    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({ date: new Date(year, month, i), day: i, isCurrentMonth: true });
    }

    const totalDisplayed = prevMonthDays.length + currentMonthDays.length;
    const nextMonthDays = [];
    for (let i = 1; totalDisplayed + nextMonthDays.length < 42; i++) {
      const d = new Date(year, month + 1, i);
      nextMonthDays.push({ date: d, day: i, isCurrentMonth: false });
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const daysOfWeek = ["SU", "M", "T", "W", "Th", "F", "SA"];


  return (
    <div className="flex flex-col bg-slate-50 w-full">
      
      {/* 1. Persistent Header with Domain Selector */}
      {!!onClose && (
        <div className={`flex flex-col items-center justify-center py-6 sm:py-10 bg-white border-b border-slate-200 shadow-sm ${onClose ? 'px-4 sm:px-8' : 'px-4 sm:px-6'}`}>
          <div className="flex flex-col gap-6 w-full max-w-md sm:min-w-[380px]">

            {/* Simple Heading */}
            {activeInterest === null && (
              <div className="text-center mb-1">
                <h2 className="text-[18px] font-bold text-slate-800">
                  Give your interest for booking a slot
                </h2>
              </div>
            )}

            {/* Event Type Dropdown */}
            {activeInterest === null && !!onClose && !forcedEventType && (
              <TextField
                select
                label="Select Event Type"
                value={formData.eventType}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    eventType: e.target.value,
                    domain: "",
                  }));
                  setSelectedDates([]); // Reset dates
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  }
                }}
              >
                <MenuItem 
                  value="fdp"
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: '#eff6ff', color: '#1d4ed8' }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">FDP (6 Days)</span>
                  </div>
                </MenuItem>
                <MenuItem 
                  value="tech_camp"
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: '#eff6ff', color: '#1d4ed8' }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Laptop className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">Tech Camp (3 Days)</span>
                  </div>
                </MenuItem>
              </TextField>
            )}

            {/* Domain Dropdown */}
            {activeInterest === null && !!onClose && formData.eventType && (
              <TextField
                select
                label="Select Domain"
                value={formData.domain}
                onChange={(e) => {
                  const selectedDomId = e.target.value.toString();
                  setFormData((prev) => ({
                    ...prev,
                    domain: selectedDomId,
                  }));
                  setSelectedDates([]); // Reset dates when domain changes to avoid issues
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                {(domainsList || []).map((dom) => (
                  <MenuItem key={dom.domain_id || dom.id} value={(dom.domain_id || dom.id).toString()}>
                    {dom.domain_name || dom.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Start Date Picker */}
            {activeInterest === null && !!onClose && formData.domain && (
              <div className="flex flex-col gap-2">
                {formData.eventType === "fdp" ? (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg p-2.5 shadow-sm">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[12px] text-red-600 font-medium leading-tight">
                      Note: FDP is a 6-day program and starts on Monday.
                    </span>
                  </div>
                ) : formData.eventType === "tech_camp" ? (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg p-2.5 shadow-sm">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[12px] text-red-600 font-medium leading-tight">
                      Note: Tech Camp is a 3-day program and starts on Monday or Thursday.
                    </span>
                  </div>
                ) : null}

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  open={isDatePickerOpen}
                  onOpen={() => setIsDatePickerOpen(true)}
                  onClose={() => setIsDatePickerOpen(false)}
                  label="Start Date"
                  value={selectedDates.length > 0 ? new Date(selectedDates[0].date) : null}
                  onChange={(newDate) => {
                    if (newDate) {
                      handleDateClick(newDate, false);
                    } else {
                      setSelectedDates([]);
                    }
                  }}
                  shouldDisableDate={(date) => isDateDisabled(date)}
                  minDate={new Date()} // Prevent scrolling to past months
                  maxDate={new Date(new Date().getFullYear() + Math.floor((new Date().getMonth() + 4) / 12), (new Date().getMonth() + 4) % 12 + 1, 0)} // Stop exactly 5 months from now
                  PopperProps={{
                    placement: "top",
                    style: { zIndex: 1300 },
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                      }
                    ]
                  }}
                  componentsProps={{
                    popper: {
                      placement: "top",
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        }
                      ]
                    }
                  }}
                  PaperProps={{
                    sx: {
                      "& .MuiPickersDay-today": {
                        backgroundColor: "#3b82f6 !important",
                        color: "#ffffff !important",
                        fontWeight: "bold",
                        border: "none"
                      }
                    }
                  }}
                  renderInput={(params) => {
                    const startDateStr = selectedDates.length > 0 ? formatDateString(new Date(selectedDates[0].date)) : "";
                    const endDateStr = selectedDates.length > 0 ? formatDateString(new Date(selectedDates[selectedDates.length - 1].date)) : "";
                    
                    return (
                      <TextField 
                        {...params}
                        onClick={() => setIsDatePickerOpen(true)}
                        label="" 
                        autoComplete="off"
                        InputLabelProps={{ shrink: true, sx: { display: "none" } }}
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true,
                          sx: { width: 0, height: 0, padding: 0, opacity: 0, pointerEvents: "none" }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: null, // Remove the default calendar icon from the far right
                          startAdornment: (
                            <div className="flex w-full items-center cursor-pointer mr-1">
                              <div className="flex-1 flex items-center justify-between">
                                <div className="flex flex-col justify-center">
                                  <span className="text-[11px] text-slate-500 mb-0.5 font-semibold">Start Date</span>
                                  <span className="text-[14px] font-medium text-slate-800">{startDateStr}</span>
                                </div>
                                <div className="mr-2">
                                  {params.InputProps.endAdornment}
                                </div>
                              </div>
                              <div className="w-px h-10 bg-slate-300 mx-2"></div>
                              <div className="flex-1 flex flex-col justify-center ml-2">
                                <span className="text-[11px] text-slate-500 mb-0.5 font-semibold">End Date</span>
                                <span className="text-[14px] font-medium text-slate-600">{endDateStr}</span>
                              </div>
                            </div>
                          )
                        }}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                          }
                        }} 
                      />
                    );
                  }}
                />
              </LocalizationProvider>
              </div>
            )}

            {/* Submit Button */}
            {activeInterest === null && !!onClose && selectedDates.length > 0 && (
              <Button
                variant="contained"
                onClick={handleBookSelectedClick}
                disabled={submitting}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: "12px",
                  bgcolor: "#4a90e2",
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px 0 rgba(74,144,226,0.39)",
                  "&:hover": {
                    bgcolor: "#357abd",
                  }
                }}
              >
                {submitting ? "Submitting..." : "Submit Interest"}
              </Button>
            )}
          </div>
          {subTabs && (
            <div className="flex items-center ml-auto">
              {subTabs}
            </div>
          )}
        </div>
      )}

      {/* 2. Main Content Area */}
      <div className="px-6 pt-6 pb-2 gap-6 flex flex-col">
        {!onClose && subTabs && (
          <div className="flex justify-end w-full mb-2">
            {subTabs}
          </div>
        )}
        {loading && !onClose ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center my-auto min-h-[250px]">
            <CircularProgress size={40} thickness={4} />
            <h3 className="text-slate-700 font-semibold text-base mt-4">Loading Bookings...</h3>
          </div>
        ) : activeInterest !== null ? (
          <div className="flex flex-col gap-6">
            {/* Banner/Status Message */}
            {/* <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-blue-800 text-sm font-medium flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span>You already have an active interest or approved booking. Additional requests are restricted.</span>
            </div> */}

            {/* Active Interest Table */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-slate-800 font-bold text-sm mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
                Interest & Booking Details
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-500 font-semibold text-xs">
                      <th className="pb-3 pr-4">Domain Name</th>
                      <th className="pb-3 px-4">Date Range</th>
                      <th className="pb-3 px-4 text-center">Status</th>
                      <th className="pb-3 pl-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 text-xs font-semibold">
                    <tr className="hover:bg-slate-50/55 transition-colors">
                      <td className="py-3.5 pr-4 text-slate-800 font-bold">{activeInterest.domain_name}</td>
                      <td className="py-3.5 px-4 text-slate-600 font-medium">
                        {`${formatDateString(activeInterest.start_date)} - ${formatDateString(activeInterest.end_date)}`}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusInfo(activeInterest.status).colorClass}`}>
                          {getStatusInfo(activeInterest.status).label}
                        </span>
                      </td>
                      <td className="py-3.5 pl-4 text-right">
                        {(activeInterest.status === "approved" ||
                          activeInterest.status === "accepted" ||
                          activeInterest.status === 1 ||
                          activeInterest.status === "1" ||
                          activeInterest.status === "payment_pending") ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setSelectedPaymentBooking(activeInterest);
                              setPaymentModalOpen(true);
                            }}
                            sx={{
                              fontSize: "11px",
                              fontWeight: 700,
                              py: 0.5,
                              px: 1.8,
                              borderRadius: "8px",
                              backgroundColor: "#1976d2",
                              boxShadow: "none",
                              textTransform: "uppercase",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                              },
                            }}
                          >
                            Pay Now
                          </Button>
                        ) : (
                          <Typography fontSize={12} color="text.secondary">
                            -
                          </Typography>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : !onClose ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center my-auto">
            <h3 className="text-slate-800 font-bold text-lg mb-2">No Active Bookings</h3>
            <p className="text-slate-500 text-sm max-w-md font-medium">
              You do not have any active bookings. Click "Book Your Slot" to submit a new interest.
            </p>
          </div>
        ) : null}
      </div>



      {/* CONFIRMATION POPUP */}
      <Dialog
        open={showConfirmPopup}
        onClose={() => setShowConfirmPopup(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 30px rgba(0,0,0,0.12)",
            p: 3,
            maxWidth: calendarAvailability?.popup_msg ? "480px" : "360px",
            width: "100%",
          },
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4 text-blue-600">
            <Calendar className="w-6 h-6" />
          </div>
          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 700, color: "#1E293B", mb: 1 }}>
            Confirm
          </Typography>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 500, color: "#64748B", mb: calendarAvailability?.popup_msg ? 2 : 3, px: 1 }}>
            Are you sure you want to submit this request?
          </Typography>

          {/* popup_msg warning panel */}
          {calendarAvailability?.popup_msg && (
            <div
              style={{
                width: "100%",
                background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                border: "1px solid #f59e0b",
                borderRadius: "10px",
                padding: "12px 14px",
                marginBottom: "20px",
                textAlign: "left",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                <AlertCircle style={{ width: 16, height: 16, color: "#d97706", flexShrink: 0, marginTop: 1 }} />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#92400e",
                    whiteSpace: "pre-line",
                    lineHeight: 1.7,
                  }}
                >
                  {calendarAvailability.popup_msg}
                </Typography>
              </div>
            </div>
          )}

          <div className="flex gap-3 w-full justify-center">
            <Button
              variant="outlined"
              onClick={() => setShowConfirmPopup(false)}
              sx={{ 
                textTransform: "none", 
                fontWeight: 600, 
                borderRadius: "10px",
                flex: 1,
                borderColor: "#E2E8F0",
                color: "#64748B",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  borderColor: "#CBD5E1",
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={async () => {
                setShowConfirmPopup(false);
                await handleSubmitBooking();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                flex: 1,
                boxShadow: "none",
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
                  boxShadow: "none",
                },
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>

      {/* TOAST NOTIFICATIONS */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* PAYMENT DETAILS MODAL */}
      <PaymentDetailsModal
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedPaymentBooking(null);
        }}
        booking={selectedPaymentBooking}
        eventType={formData.eventType || "tech_camp"}
      />
    </div>
  );
};

export default AdvanceBook;
